import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { UserPlus, Lock, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
}

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#0a0a0e] px-4 py-3 text-white placeholder:text-neutral-600 outline-none transition-colors focus:border-[#e6b800]/70";

export default function AuthForms({ onAuthed, note, showOwnerLink = true }) {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "register") {
        await api.post("/auth/customer/register", form, { withCredentials: true });
        toast.success("Account बन गया — स्वागत है!");
      } else {
        await api.post("/auth/login", { email: form.email, password: form.password }, { withCredentials: true });
        toast.success("Login हो गया");
      }
      onAuthed();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-md" data-testid="account-auth">
      <div className="mx-auto flex max-w-xs rounded-full border border-white/10 bg-[#101014] p-1" data-testid="account-auth-tabs">
        {[
          { id: "login", label: "Login" },
          { id: "register", label: "नया account" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setMode(t.id)}
            className={`flex-1 rounded-full px-5 py-2 font-mono2 text-[11px] uppercase tracking-[0.15em] transition-colors duration-200 ${
              mode === t.id ? "bg-[#e6b800] font-bold text-black" : "text-neutral-400 hover:text-white"
            }`}
            data-testid={`account-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <motion.form
        key={mode}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        onSubmit={submit}
        className="card-glow mt-6 rounded-2xl p-8"
        data-testid="account-auth-form"
      >
        {mode === "register" && (
          <div className="mb-4">
            <label htmlFor="acc-name" className="mb-2 block font-mono2 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
              नाम
            </label>
            <input id="acc-name" data-testid="account-name-input" type="text" className={inputCls} value={form.name} onChange={set("name")} placeholder="आपका नाम" minLength={2} required />
          </div>
        )}
        <div className="mb-4">
          <label htmlFor="acc-email" className="mb-2 block font-mono2 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
            Email
          </label>
          <input id="acc-email" data-testid="account-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="aap@example.com" required />
          {note ? (
            <p className="mt-2 text-[11px] leading-relaxed text-neutral-500">{note}</p>
          ) : (
            <p className="mt-2 text-[11px] leading-relaxed text-neutral-500">
              जिस email से booking की थी, वही इस्तेमाल करें — आपकी bookings अपने-आप यहाँ दिखेंगी।
            </p>
          )}
        </div>
        <div className="mb-6">
          <label htmlFor="acc-pass" className="mb-2 block font-mono2 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
            Password (min 8 characters)
          </label>
          <div className="relative">
            <input id="acc-pass" data-testid="account-password-input" type={showPass ? "text" : "password"} className={`${inputCls} pr-12`} value={form.password} onChange={set("password")} minLength={8} required />
            <button
              type="button"
              onClick={() => setShowPass((v) => !v)}
              data-testid="account-password-toggle"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 transition-colors hover:text-[#e6b800]"
              aria-label={showPass ? "Hide password" : "Show password"}
            >
              {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="account-submit-button"
          className="flex w-full items-center justify-center gap-2 rounded-full bg-[#e6b800] py-3.5 font-bold text-[#070709] transition-all duration-200 hover:bg-[#ffc700] disabled:opacity-50"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : mode === "register" ? <UserPlus size={15} /> : <Lock size={15} />}
          {loading ? "Please wait..." : mode === "register" ? "Account बनाएँ" : "Login करें"}
        </button>
        {mode === "login" && (
          <p className="mt-3 text-center text-[11px] text-neutral-500">
            <Link to="/forgot-password" data-testid="account-forgot-link" className="text-[#e6b800] hover:underline">
              Password भूल गए?
            </Link>
          </p>
        )}
        {showOwnerLink && (
          <p className="mt-4 text-center text-[11px] text-neutral-500">
            Owner हैं?{" "}
            <Link to="/admin" data-testid="account-owner-link" className="text-[#e6b800] hover:underline">
              यहाँ से login करें
            </Link>
          </p>
        )}
      </motion.form>
    </div>
  );
}
